import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

const initials = (name) => (name || "S").trim().charAt(0).toUpperCase();
const timeShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

// Buyer messaging center — lists seller conversations and shows the active thread inline.
export const BuyerMessages = () => {
  const { user } = useAuth();
  const { conversations, messages, activeId, openConversation, sendMessage, sendTyping, typingPeer, loadConversations } = useChat();
  const [draft, setDraft] = useState("");
  const [mobileThread, setMobileThread] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { loadConversations?.(); }, [loadConversations]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeId]);

  const active = conversations.find((c) => c.conversation_id === activeId);

  const open = (id) => { openConversation(id, false); setMobileThread(true); };
  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    sendMessage(text);
    setDraft("");
  };

  return (
    <Card className="p-0 overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="buyer-messages">
      <div className="grid md:grid-cols-[300px_1fr] h-[70vh] min-h-[460px]">
        {/* Conversation list */}
        <div className={`border-r border-border/60 flex flex-col ${mobileThread ? "hidden md:flex" : "flex"}`}>
          <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-orange-500" />
            <span className="font-semibold text-sm">Conversations</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <ChatBubbleLeftRightIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                No messages yet. Start a chat from a product page or from one of your orders.
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.conversation_id}
                data-testid={`buyer-conv-${c.conversation_id}`}
                onClick={() => open(c.conversation_id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-border/40 transition-colors
                  ${activeId === c.conversation_id ? "bg-orange-50" : "hover:bg-muted/50"}`}
              >
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center font-bold shrink-0">
                  {initials(c.peer?.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{c.peer?.name || "Seller"}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.last_message || "No messages yet"}</p>
                </div>
                {c.unread > 0 && (
                  <span className="ml-1 shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`buyer-conv-unread-${c.conversation_id}`}>
                    {c.unread}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`flex flex-col ${mobileThread ? "flex" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
              <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">Select a conversation to start chatting</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
                <button className="md:hidden" onClick={() => setMobileThread(false)} data-testid="buyer-messages-back" aria-label="Back">
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center font-bold">
                  {initials(active.peer?.name)}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{active.peer?.name || "Seller"}</p>
                  {active.product_title && <p className="text-xs text-muted-foreground truncate">Re: {active.product_title}</p>}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-orange-500 text-white rounded-br-sm" : "bg-white dark:bg-card border rounded-bl-sm"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{timeShort(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                {typingPeer && typingPeer !== user?.user_id && (
                  <p className="text-xs text-muted-foreground italic">typing…</p>
                )}
              </div>

              <form onSubmit={submit} className="p-3 border-t border-border/60 flex items-center gap-2">
                <Input
                  data-testid="buyer-message-input"
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); sendTyping?.(); }}
                  placeholder="Type a message…"
                  className="rounded-full"
                />
                <Button type="submit" size="icon" className="rounded-full bg-orange-500 hover:bg-orange-600 shrink-0" data-testid="buyer-message-send" aria-label="Send">
                  <PaperAirplaneIcon className="w-5 h-5" />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
